import { memo, useCallback, useSyncExternalStore } from 'react';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { selectSpeakerAnalysis } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import { classnames } from '@/libs/utils';
import {
  getFaceLocationUrl,
  getFullVideoFaceLocationUrl
} from '@/Pages/Clip/FaceSpeakerToggler/FaceSpeakerTogglerUtils';
import { core } from '@/stores/core';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { isFullVideoFaceRecEnabled } from '@/stores/clip';

function FacesGrid({
  speakerInfo,
  onSpeakerUpdate,
  clipId,
  broadcastId
}: {
  speakerInfo: SpeakerWithDetails;
  onSpeakerUpdate: (key: string, value: number[]) => void;
  clipId: string;
  broadcastId: string;
}) {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const content = coreStore.content;
  const speakersAnalysis = useContentMetadataStore(selectSpeakerAnalysis(broadcastId));
  const faceIds = speakersAnalysis?.face_ids ?? [];
  const speakerInfoFaceIds = speakerInfo.face_ids || [];

  // In the full-video face-rec flow, the Vision backend stores a single representative
  // crop per face under a `fullvideo` subdirectory (keyed by content id). In the per-clip
  // flow the crops live under the clip id segment.
  const buildFaceUrl = useCallback(
    (faceId: number) => {
      if (!content) {
        return '';
      }
      return isFullVideoFaceRecEnabled()
        ? getFullVideoFaceLocationUrl(content.project_id, content.id, faceId)
        : getFaceLocationUrl(content.project_id, content.id, clipId, faceId);
    },
    [content, clipId]
  );

  const handleFaceIdToggle = faceId => {
    onSpeakerUpdate(
      'face_ids',
      speakerInfoFaceIds?.length > 0 && speakerInfoFaceIds.includes(faceId)
        ? speakerInfoFaceIds.filter((id: number) => id !== faceId)
        : [...speakerInfoFaceIds, faceId]
    );
  };

  if (!content) {
    return null;
  }

  return (
    faceIds?.length > 0 && (
      <div className="border-b p-2 pb-4">
        <div className="mt-4 text-sm font-medium leading-tight">Select the speaker‘s face</div>
        <div className="mt-3 grid grid-cols-7 gap-3">
          {faceIds.map((faceId: number) => (
            <img
              key={faceId}
              src={buildFaceUrl(faceId)}
              alt={faceId.toString()}
              className={classnames(
                'aspect-square w-full cursor-pointer rounded-full border-2 border-slate-200 bg-cover bg-center',
                {
                  '!border-deep-orange-600': speakerInfoFaceIds?.includes(faceId),
                  'opacity-50': speakerInfoFaceIds?.length > 0 && !speakerInfoFaceIds?.includes(faceId)
                }
              )}
              onClick={() => handleFaceIdToggle(faceId)}
            />
          ))}
        </div>
      </div>
    )
  );
}

export default memo(FacesGrid);
